import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ELSI_REMINDERS_DEFAULT_POLICY,
  ELSI_REMINDERS_HIDE_SMS,
  ELSI_REMINDERS_LIMITS,
  ElsiReminderPolicy,
  ElsiReminderPolicyOverride,
} from '@et/typings';
import { InputModule } from '../input/input.module';

@Component({
  selector: 'et-atoms-elsi-reminder-edit',
  imports: [ReactiveFormsModule, InputModule],
  templateUrl: './elsi-reminder-edit.component.html',
  styleUrl: './elsi-reminder-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElsiReminderEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  policy = input.required<ElsiReminderPolicy>();
  saved = output<ElsiReminderPolicyOverride>();
  cancelled = output<void>();

  protected hideSms = ELSI_REMINDERS_HIDE_SMS;
  protected limits = ELSI_REMINDERS_LIMITS;

  private readonly emailOrSmsRequired: ValidatorFn = (control) => {
    const email = control.get('emailEnabled');
    const sms = control.get('smsEnabled');
    return email?.value || sms?.value ? null : { emailOrSmsRequired: true };
  };

  form = this.fb.group(
    {
      firstDelayDays: this.fb.nonNullable.control(
        ELSI_REMINDERS_DEFAULT_POLICY.firstDelayDays,
        [
          Validators.required,
          Validators.min(ELSI_REMINDERS_LIMITS.firstDelayDays.min),
          Validators.max(ELSI_REMINDERS_LIMITS.firstDelayDays.max),
        ],
      ),
      intervalDays: this.fb.nonNullable.control(
        ELSI_REMINDERS_DEFAULT_POLICY.intervalDays,
        [
          Validators.required,
          Validators.min(ELSI_REMINDERS_LIMITS.intervalDays.min),
          Validators.max(ELSI_REMINDERS_LIMITS.intervalDays.max),
        ],
      ),
      maxRounds: this.fb.nonNullable.control(
        ELSI_REMINDERS_DEFAULT_POLICY.maxRounds,
        [
          Validators.required,
          Validators.min(ELSI_REMINDERS_LIMITS.maxRounds.min),
          Validators.max(ELSI_REMINDERS_LIMITS.maxRounds.max),
        ],
      ),
      emailEnabled: this.fb.nonNullable.control(
        this.hideSms ? true : ELSI_REMINDERS_DEFAULT_POLICY.emailEnabled,
      ),
      smsEnabled: this.fb.nonNullable.control(
        this.hideSms ? false : ELSI_REMINDERS_DEFAULT_POLICY.smsEnabled,
      ),
    },
    { validators: [this.emailOrSmsRequired] },
  );

  constructor() {
    effect(() => {
      const policy = this.policy();
      this.form.patchValue(
        {
          firstDelayDays: policy.firstDelayDays,
          intervalDays: policy.intervalDays,
          maxRounds: policy.maxRounds,
          emailEnabled: policy.emailEnabled,
          smsEnabled: policy.smsEnabled,
        },
        { emitEvent: false },
      );
    });
  }

  ngOnInit(): void {
    if (this.hideSms) {
      // Disable email checkbox to make it always checked
      this.form.controls.emailEnabled.disable();
    }
  }

  /**
   * Emits the override when the form is valid.
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.saved.emit({
      firstDelayDays: value.firstDelayDays,
      intervalDays: value.intervalDays,
      maxRounds: value.maxRounds,
      emailEnabled: value.emailEnabled,
      smsEnabled: value.smsEnabled,
    });
  }
}
